import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Eye, Edit, Trash2, Users, Mail, Phone, TrendingUp, Loader, ArrowRightLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { useApiData } from '../../hooks/useApi';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import TransferModal from '../common/TransferModal';

const TeamLeads = ({ user, selectedCountry = 'all' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [availablePartners, setAvailablePartners] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringLead, setTransferringLead] = useState(null);

  const { data: teamLeadsData, loading, refetch } = useApiData(
    () => {
      const params = { search: searchTerm };
      if (selectedCountry && selectedCountry !== 'all') {
        params.country_id = selectedCountry;
      }
      return apiService.getTeamLeads(params);
    },
    [searchTerm, selectedCountry]
  );

  const teamLeads = Array.isArray(teamLeadsData?.data) ? teamLeadsData.data :
    Array.isArray(teamLeadsData) ? teamLeadsData : [];

  // Listen for dashboard quick action events
  useEffect(() => {
    const handleOpenModal = () => {
      setShowAddModal(true);
      setEditingLead(null);
      setFormData({ name: '', email: '', phone: '' });
    };

    window.addEventListener('open-team-lead-modal', handleOpenModal);
    return () => window.removeEventListener('open-team-lead-modal', handleOpenModal);
  }, []);

  const handleView = (lead) => {
    setSelectedLead(lead);
    setShowViewModal(true);
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email,
      phone: lead.phone || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = (lead) => {
    setDeletingLead(lead);
    setShowDeleteModal(true);
  };

  const handleManageTeam = async (lead) => {
    setSelectedLead(lead);
    try {
      const [partnersRes, membersRes] = await Promise.all([
        apiService.get(`/team-leads/${lead.id}/available-partners`),
        apiService.get(`/team-leads/${lead.id}/members`)
      ]);
      setAvailablePartners(partnersRes.data || []);
      setTeamMembers(membersRes.data || []);
      setShowTeamModal(true);
    } catch (error) {
      toast.error('Failed to load team data');
    }
  };

  const handleTransfer = (lead) => {
    setTransferringLead(lead);
    setShowTransferModal(true);
  };

  const handleTransferComplete = () => {
    refetch();
    setShowTransferModal(false);
    setTransferringLead(null);
  };

  const handleAssignPartner = async () => {
    if (!selectedPartner) return;
    try {
      await apiService.post(`/team-leads/${selectedLead.id}/assign`, {
        referralPartnerId: selectedPartner
      });
      toast.success('Team member assigned successfully');
      // Refresh data
      const [partnersRes, membersRes] = await Promise.all([
        apiService.get(`/team-leads/${selectedLead.id}/available-partners`),
        apiService.get(`/team-leads/${selectedLead.id}/members`)
      ]);
      setAvailablePartners(partnersRes.data || []);
      setTeamMembers(membersRes.data || []);
      setSelectedPartner('');
    } catch (error) {
      toast.error(error.message || 'Failed to assign team member');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await apiService.delete(`/team-leads/${selectedLead.id}/members/${memberId}`);
      toast.success('Team member removed successfully');
      // Refresh data
      const [partnersRes, membersRes] = await Promise.all([
        apiService.get(`/team-leads/${selectedLead.id}/available-partners`),
        apiService.get(`/team-leads/${selectedLead.id}/members`)
      ]);
      setAvailablePartners(partnersRes.data || []);
      setTeamMembers(membersRes.data || []);
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteTeamLead(deletingLead.id);
      toast.success('Academic Counsellor deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error.message || 'Failed to delete Academic Counsellor');
    } finally {
      setShowDeleteModal(false);
      setDeletingLead(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = { ...formData };
      if (selectedCountry && selectedCountry !== 'all') {
        submitData.country_id = selectedCountry;
      }

      if (editingLead) {
        await apiService.updateTeamLead(editingLead.id, submitData);
        toast.success('Academic Counsellor updated successfully');
        setEditingLead(null);
        setShowAddModal(false);
      } else {
        await apiService.createTeamLead(submitData);
        toast.success('Academic Counsellor created successfully');
        setShowAddModal(false);
      }
      setFormData({ name: '', email: '', phone: '' });
      refetch();
    } catch (error) {
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (lead) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div className="user-avatar" style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}>
            {lead.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
          </div>
          <span>{lead.name}</span>
        </div>
      )
    },
    { key: 'email', label: 'Email' },
    {
      key: 'teamMembers',
      label: 'Team Members',
      render: (lead) => (
        <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{lead.teamMembers || 0}</span>
      )
    },
    {
      key: 'admissionCount',
      label: 'Admission Enquiries',
      render: (lead) => (
        <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{lead.admissionCount || 0}</span>
      )
    },

    {
      key: 'actions',
      label: 'Actions',
      render: (lead) => (
        <div className="action-buttons">
          <motion.button
            className="btn-sm touch-target"
            onClick={() => handleView(lead)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View Details"
          >
            <Eye size={12} />
          </motion.button>
          <motion.button
            className="btn-sm primary touch-target"
            onClick={() => handleManageTeam(lead)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Manage Team"
          >
            <Users size={12} />
          </motion.button>
          {(user?.role === 'super_admin' || user?.role === 'admin') && (
            <>
              {/* <motion.button
                className="btn-sm info touch-target"
                onClick={() => handleTransfer(lead)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Transfer Team Lead"
              >
                <ArrowRightLeft size={12} />
              </motion.button> */}
              <motion.button
                className="btn-sm warning touch-target"
                onClick={() => handleEdit(lead)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Edit"
              >
                <Edit size={12} />
              </motion.button>
              <motion.button
                className="btn-sm danger touch-target"
                onClick={() => handleDelete(lead)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title="Delete"
              >
                <Trash2 size={12} />
              </motion.button>
            </>
          )}
        </div>
      )
    }
  ];



  return (
    <>
      <div className="section-header">
        <h2>Academic Counsellor</h2>
        {(user?.role === 'super_admin' || user?.role === 'admin') && (
          <motion.button
            className="btn-primary touch-target"
            onClick={() => {
              setShowAddModal(true);
              setEditingLead(null);
              setFormData({ name: '', email: '', phone: '' });
            }}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={16} />
            <span className="responsive-text">Add Academic Counsellor</span>
          </motion.button>
        )}
      </div>

      <DataTable
        data={teamLeads}
        columns={columns}
        loading={loading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}

      />

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Academic Counsellor Details"
      >
        {selectedLead && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                {selectedLead.name?.split(' ').filter(n => n).map(n => n[0]).join('') || '?'}
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedLead.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>Academic Counsellor</p>
            </div>

            {/* Contact Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Contact Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <Mail size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151' }}>{selectedLead.email}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <Phone size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151' }}>{selectedLead.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Team Statistics</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem' }}>
                <div style={{
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <Users size={24} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                    {selectedLead.teamMembers || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', opacity: '0.9' }}>Team Members</div>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#dcfce7',
                color: '#166534',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{ width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%' }} />
                Active
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingLead(null);
          setFormData({ name: '', email: '', phone: '' });
        }}
        title={editingLead ? 'Edit Academic Counsellor' : 'Add Academic Counsellor'}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowAddModal(false);
                setEditingLead(null);
                setFormData({ name: '', email: '', phone: '' });
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader className="spin" size={16} />
                  Saving...
                </>
              ) : (
                editingLead ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingLead(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this Academic Counsellor ?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-sm danger"
              onClick={confirmDelete}
            >
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingLead(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* Team Management Modal */}
      <Modal
        isOpen={showTeamModal}
        onClose={() => {
          setShowTeamModal(false);
          setSelectedLead(null);
          setSelectedPartner('');
        }}
        title={`Manage Team - ${selectedLead?.name}`}
      >
        <div className="team-management">
          {/* Assign New Member */}
          <div className="form-group">
            <label>Assign Referral Partner</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select
                value={selectedPartner}
                onChange={(e) => setSelectedPartner(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="">Select a referral partner...</option>
                {availablePartners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name} ({partner.email})
                  </option>
                ))}
              </select>
              <button
                className="btn-primary"
                onClick={handleAssignPartner}
                disabled={!selectedPartner}
              >
                Assign
              </button>
            </div>
          </div>

          {/* Current Team Members */}
          <div className="form-group">
            <label>Current Team Members ({teamMembers.length})</label>
            {teamMembers.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No team members assigned</p>
            ) : (
              <div className="team-members-list">
                {teamMembers.map(member => (
                  <div key={member.id} className="team-member-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div>
                      <div style={{ fontWeight: '500' }}>{member.ReferralPartner?.name}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {member.ReferralPartner?.email}
                      </div>
                    </div>
                    <button
                      className="btn-sm danger"
                      onClick={() => handleRemoveMember(member.referral_partner_id)}
                      title="Remove from team"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferringLead(null);
        }}
        transferType="team_lead"
        fromUser={transferringLead}
        onTransferComplete={handleTransferComplete}
      />
    </>
  );
};

export default TeamLeads;